/**
 * Text blast — zero-setup SMS from the owner's own phone.
 *
 * No Twilio, no account, no cost: each row opens the phone's Messages app
 * with the recipient and a personalised message (including their one-tap
 * log link) pre-filled. Tap send, come back, next person. Progress is
 * remembered in localStorage so a blast can be finished in two sittings.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { PaperPlaneTilt, ChatCircleText, Copy, CheckCircle, ArrowClockwise } from "@phosphor-icons/react";
import CollapsibleSection from "@/components/admin/CollapsibleSection";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const SENT_KEY = "sws_text_blast_sent";

const DEFAULT_TEMPLATE =
  "Hey {first_name}! Sweat for Sacrifice starts now — 911 minutes of movement for Uncle Harry & Uncle Timmy. " +
  "Your private log link (no password, just tap): {link}\n" +
  "Or log any time at {site}/log with this phone number or your email. Let's go!";

export default function TextBlastPanel({ token }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [template, setTemplate] = useState(DEFAULT_TEMPLATE);
  const [sent, setSent] = useState({});
  const [copied, setCopied] = useState("");

  const headers = { "X-Admin-Token": token };
  const site = typeof window !== "undefined" ? window.location.origin : "";

  useEffect(() => {
    try { setSent(JSON.parse(localStorage.getItem(SENT_KEY) || "{}")); } catch { /* ignore */ }
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(`${BACKEND}/api/admin/text-blast/list`, { headers });
      setData(await r.json());
    } catch (e) {
      setData({ participants: [], error: e?.message });
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const message = (p) =>
    template
      .replaceAll("{first_name}", p.first_name || "there")
      .replaceAll("{name}", p.name || "there")
      .replaceAll("{link}", `${site}/log?t=${p.log_token}`)
      .replaceAll("{site}", site.replace(/^https?:\/\//, ""));

  const markSent = (id) => {
    const next = { ...sent, [id]: new Date().toISOString() };
    setSent(next);
    try { localStorage.setItem(SENT_KEY, JSON.stringify(next)); } catch { /* ignore */ }
  };

  const copy = async (text, key) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(key);
      setTimeout(() => setCopied(""), 1800);
    } catch { /* clipboard blocked */ }
  };

  const people = data?.participants || [];
  const textable = useMemo(() => people.filter((p) => p.textable), [people]);
  const sentCount = textable.filter((p) => sent[p.id]).length;

  return (
    <CollapsibleSection
      id="text-blast"
      testId="text-blast-panel"
      title="Text blast"
      icon={<ChatCircleText size={20} weight="bold" className="text-[#FF1B91]" />}
      subtitle="Text every participant their own one-tap log link from your phone — no SMS account needed."
      onRefresh={load}
    >
      <div className="space-y-5">
        <div className="flex flex-wrap items-center gap-3">
          <button
            type="button"
            data-testid="load-text-blast-btn"
            onClick={load}
            disabled={loading}
            className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.25em] font-bold border border-[#FF1B91] text-white bg-[#FF1B91]/10 hover:bg-[#FF1B91]/25 disabled:opacity-40 px-4 py-2"
          >
            <ArrowClockwise size={12} weight="bold" className={loading ? "animate-spin" : ""} />
            {loading ? "Loading…" : data ? "Reload list" : "Load participants"}
          </button>
          {data && (
            <span className="text-[11px] text-white/55" data-testid="text-blast-counts">
              <b className="text-white">{textable.length}</b> textable · <b className="text-white">{sentCount}</b> texted
              {data.no_phone?.length ? ` · ${data.no_phone.length} without a phone` : ""}
            </span>
          )}
          {sentCount > 0 && (
            <button
              type="button"
              data-testid="reset-sent-btn"
              onClick={() => { setSent({}); try { localStorage.removeItem(SENT_KEY); } catch { /* ignore */ } }}
              className="text-[10px] uppercase tracking-[0.25em] text-white/40 hover:text-[#FF1B91]"
            >
              Reset ticks
            </button>
          )}
        </div>

        <div>
          <div className="text-[9px] uppercase tracking-[0.25em] text-white/50 font-bold mb-2">
            Message · {"{first_name}"} {"{link}"} {"{site}"} are filled in automatically
          </div>
          <textarea
            data-testid="text-blast-template"
            value={template}
            onChange={(e) => setTemplate(e.target.value)}
            rows={4}
            className="w-full bg-black/50 border border-white/15 focus:border-[#FF1B91] outline-none px-3 py-2 text-sm text-white"
          />
          {people[0] && (
            <div className="mt-2 text-[11px] text-white/50 border border-white/10 bg-black/30 px-3 py-2" data-testid="text-blast-preview">
              <span className="text-white/35 uppercase tracking-[0.2em] text-[9px]">Preview for {people[0].name}: </span>
              {message(people[0])}
            </div>
          )}
        </div>

        {textable.length > 0 && (
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              data-testid="copy-all-numbers-btn"
              onClick={() => copy(textable.map((p) => p.sms_number).join(", "), "numbers")}
              className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.25em] font-bold border border-white/25 text-white hover:border-[#FF1B91] px-3 py-2"
            >
              <Copy size={12} weight="bold" /> {copied === "numbers" ? "Copied!" : "Copy all numbers"}
            </button>
            <button
              type="button"
              data-testid="copy-generic-message-btn"
              onClick={() => copy(
                template
                  .replaceAll("{first_name}", "there")
                  .replaceAll("{name}", "there")
                  .replaceAll("{link}", `${site}/log`)
                  .replaceAll("{site}", site.replace(/^https?:\/\//, "")),
                "generic")}
              className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.25em] font-bold border border-white/25 text-white hover:border-[#FF1B91] px-3 py-2"
            >
              <Copy size={12} weight="bold" /> {copied === "generic" ? "Copied!" : "Copy group-text version"}
            </button>
          </div>
        )}

        {textable.length > 0 && (
          <div className="border border-white/10" data-testid="text-blast-list">
            <div className="overflow-x-auto max-h-[560px]">
              <table className="w-full text-[11px]">
                <thead className="sticky top-0 bg-[#0F0F0F]">
                  <tr className="text-white/40 uppercase tracking-[0.15em] text-[9px] border-b border-white/10">
                    <th className="p-2 text-left">Name</th>
                    <th className="p-2 text-left">Phone</th>
                    <th className="p-2 text-right">Minutes</th>
                    <th className="p-2 text-right">Send</th>
                  </tr>
                </thead>
                <tbody>
                  {textable.map((p) => (
                    <tr key={p.id} data-testid={`text-row-${p.id}`} className={`border-b border-white/5 ${sent[p.id] ? "opacity-45" : "hover:bg-white/[0.03]"}`}>
                      <td className="p-2 text-white whitespace-nowrap">{p.name}</td>
                      <td className="p-2 text-white/55 whitespace-nowrap font-mono">{p.phone || p.phone_digits}</td>
                      <td className="p-2 text-right text-white/60">{p.minutes_logged}</td>
                      <td className="p-2 text-right whitespace-nowrap">
                        <a
                          data-testid={`text-btn-${p.id}`}
                          href={`sms:${p.sms_number}${/iPhone|iPad|Macintosh/i.test(navigator.userAgent) ? "&" : "?"}body=${encodeURIComponent(message(p))}`}
                          onClick={() => markSent(p.id)}
                          className="inline-flex items-center gap-1.5 text-[9px] uppercase tracking-[0.2em] font-bold border border-[#FF1B91] text-white bg-[#FF1B91]/10 hover:bg-[#FF1B91]/30 px-3 py-1.5"
                        >
                          <PaperPlaneTilt size={11} weight="bold" /> Text
                        </a>
                        <button
                          type="button"
                          data-testid={`copy-btn-${p.id}`}
                          onClick={() => copy(message(p), p.id)}
                          className="ml-2 text-[9px] uppercase tracking-[0.2em] text-white/40 hover:text-[#FF1B91]"
                        >
                          {copied === p.id ? "Copied" : "Copy"}
                        </button>
                        {sent[p.id] && <CheckCircle size={14} weight="fill" className="inline ml-2 text-emerald-400" />}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {data?.no_phone?.length > 0 && (
          <div className="text-[11px] text-white/50 border border-white/10 bg-black/20 px-3 py-2" data-testid="no-phone-list">
            <span className="text-[9px] uppercase tracking-[0.25em] text-white/40 font-bold">No phone on file — email these {data.no_phone.length}: </span>
            {data.no_phone.join(" · ")}
          </div>
        )}

        <div className="text-[11px] text-white/45 border-t border-white/10 pt-3 leading-relaxed">
          <b className="text-white/70">Tip:</b> open <span className="font-mono">{site.replace(/^https?:\/\//, "")}/admin</span> on your phone,
          then tap "Text" on each row — Messages opens with the person and their personal link already filled in. The tick marks
          your progress, so you can stop and pick up where you left off.
        </div>
      </div>
    </CollapsibleSection>
  );
}
